// Settings › Agents and tokens › Connected agents (plan step 4.1; D98; APS-310): the grants that
// let an OAuth client (an MCP client, named by its client_id) write in this organization on your
// behalf, one capability per grant: create and edit dashboards, or change issue states and
// assignees (APS-225), each with what it called in this project this week (APS-300's summary).
// AuthKit cannot give such clients a custom scope, so you give it here, from the console only:
// an agent's refused write links here with its client and capability filled in.
// A client_id is untrusted text (a URL for clients that describe themselves): it is shown as
// text, never as a link. Members see and revoke their own grants; owners and admins everyone's.
// A grant unused for 90 days expires on its own.
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useId, useRef, useState } from 'react'
import { isApiError } from '../api/client'
import {
  agentGrantsQuery,
  meQuery,
  orgQuery,
  useCreateAgentGrant,
  useRevokeAgentGrant,
  useRevokeAllAgentGrants,
} from '../api/queries'
import type { AgentSummary } from '../api/types'
import { Confirm } from '../components/Confirm'
import { Btn, btnClass, EmptyNote, Section, Table, TableFoot } from '../components/d'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { AgentName } from './Agents'
import { AGENT_CAPABILITIES, type AgentCapability } from './search'

/** The API's rule for a client_id: printable ASCII, 1 to 256 characters. */
const CLIENT_ID = /^[!-~]{1,256}$/

/** Each capability's label, the verb a confirmation says, and the list's short name. */
const CAPABILITY = {
  'dashboards:write': {
    label: 'settings.agents.cap.dashboards',
    verb: 'settings.agents.verb.dashboards',
    short: 'settings.agents.access.dashboards',
  },
  'issues:write': {
    label: 'settings.agents.cap.issues',
    verb: 'settings.agents.verb.issues',
    short: 'settings.agents.access.issues',
  },
  // Only an owner or admin may hold these (D117, D115): a member sees them, unavailable, and why.
  'instrumentation:write': {
    label: 'settings.agents.cap.instrumentation',
    verb: 'settings.agents.verb.instrumentation',
    short: 'settings.agents.access.instrumentation',
  },
  'exports:write': {
    label: 'settings.agents.cap.exports',
    verb: 'settings.agents.verb.exports',
    short: 'settings.agents.access.exports',
  },
} as const

/** What only an owner or admin may grant, since only they may do it (D117, D115). */
const MANAGER_ONLY: readonly AgentCapability[] = ['instrumentation:write', 'exports:write']

export function ConnectedAgents({
  orgId,
  projectId,
  summary,
  client,
  capability,
}: {
  orgId: string
  projectId: string
  /** This project's agent calls this week, for each grant's last call and count. */
  summary: AgentSummary | undefined
  client?: string | undefined
  capability?: AgentCapability | undefined
}) {
  const { t, date, number, relative } = useI18n()
  const org = useQuery(orgQuery(orgId))
  const me = useQuery(meQuery)
  const grants = useQuery(agentGrantsQuery(orgId))
  const revoke = useRevokeAgentGrant(orgId)
  const revokeAll = useRevokeAllAgentGrants(orgId)
  const admin = org.data?.role === 'owner' || org.data?.role === 'admin'
  const orgName = org.data?.name ?? ''
  const rows = grants.data ?? []
  const mine = rows.filter((g) => g.user_id === me.data?.user.id)
  const calls = new Map(
    (summary?.clients ?? []).filter((c) => c.kind === 'oauth').map((c) => [c.id, c]),
  )
  const when = (value: string | null) =>
    value ? date(value, { dateStyle: 'medium' }) : t('settings.agents.never')

  return (
    <Section
      title={t('settings.agents.connected')}
      meta={grants.data ? number(rows.length) : undefined}
      right={
        <GrantDialog
          orgId={orgId}
          orgName={orgName}
          client={client}
          capability={capability}
          admin={admin}
        />
      }
    >
      {grants.isPending ? (
        <Loading />
      ) : grants.isError ? (
        <ErrorState error={grants.error} retry={() => grants.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyNote title={t('settings.agents.none')}>{t('settings.agents.noneBody')}</EmptyNote>
      ) : (
        <>
          <Table label={admin ? t('settings.agents.listAll') : t('settings.agents.list')}>
            <thead>
              <tr>
                <th className="f">{t('settings.agents.col.client')}</th>
                <th>{t('settings.agents.col.access')}</th>
                {admin ? <th>{t('settings.agents.col.user')}</th> : null}
                <th>{t('settings.agents.col.lastCall')}</th>
                <th className="num">{t('settings.agents.col.calls')}</th>
                <th>{t('settings.agents.col.expires')}</th>
                <th className="last">
                  <span className="visually-hidden">{t('settings.keys.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => {
                const c = calls.get(g.client_id)
                return (
                  <tr key={g.id}>
                    <td className="f wrap">
                      <AgentName className="se-who" name={g.client_name || g.client_id} />
                      {/* How it connects; its client id (text, never a link: the client wrote
                          it, D98) on hover. */}
                      <span className="t2" title={g.client_id}>
                        {c?.last_call.via === 'mcp'
                          ? `${t('agents.via.mcp')} · ${t('agents.via.oauth')}`
                          : t('agents.via.oauth')}
                      </span>
                    </td>
                    <td>
                      {t(CAPABILITY[g.capability].short)}
                      <span className="t2">
                        {g.last_used_at
                          ? t('settings.agents.usedOn', { date: when(g.last_used_at) })
                          : t('settings.agents.neverUsed')}
                      </span>
                    </td>
                    {admin ? <td>{g.user_name}</td> : null}
                    <td>
                      {c ? (
                        <>
                          {relative(c.last_call.at)}
                          <span className="t2" translate="no">
                            {c.last_call.tool ?? c.last_call.route}
                          </span>
                        </>
                      ) : (
                        <span className="faint">{t('settings.agents.noCall')}</span>
                      )}
                    </td>
                    <td className="num">{number(c?.calls ?? 0)}</td>
                    <td>{when(g.expires_at)}</td>
                    <td className="last">
                      <Confirm
                        buttonClass={btnClass('ghost', 'sm')}
                        label={t('settings.agents.revoke')}
                        title={t('settings.agents.revokeTitle')}
                        body={t('settings.agents.revokeBody', {
                          client: g.client_name || g.client_id,
                          access: t(CAPABILITY[g.capability].verb),
                        })}
                        confirm={t('settings.agents.revokeConfirm')}
                        busy={revoke.isPending}
                        onConfirm={() => revoke.mutate(g.id)}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
          <TableFoot>
            <span className="se-hint">{t('settings.agents.listSub')}</span>
            <span className="se-who" style={{ flexWrap: 'wrap' }}>
              {admin ? (
                <Link
                  className={btnClass('ghost', 'sm')}
                  to="/o/$orgId/p/$projectId/settings/$section"
                  params={{ orgId, projectId, section: 'activity' }}
                >
                  {t('settings.agents.auditLog')}
                </Link>
              ) : null}
              <Confirm
                buttonClass={btnClass('ghost', 'sm')}
                label={t('settings.agents.revokeAllMine')}
                title={t('settings.agents.revokeAllTitle')}
                body={t('settings.agents.revokeAllBody', { org: orgName })}
                confirm={t('settings.agents.revokeConfirm')}
                busy={revokeAll.isPending}
                disabled={mine.length === 0}
                onConfirm={() => revokeAll.mutate(false)}
              />
              {admin ? (
                <Confirm
                  buttonClass={btnClass('ghost', 'sm')}
                  label={t('settings.agents.revokeAllEveryone')}
                  title={t('settings.agents.revokeAllTitle')}
                  body={t('settings.agents.revokeEveryoneBody', { org: orgName })}
                  confirm={t('settings.agents.revokeConfirm')}
                  busy={revokeAll.isPending}
                  onConfirm={() => revokeAll.mutate(true)}
                />
              ) : null}
            </span>
          </TableFoot>
        </>
      )}
      {revoke.error || revokeAll.error ? (
        <p role="alert" className="notice error">
          {t('state.failed')}
        </p>
      ) : null}
      {revokeAll.isSuccess ? (
        <p role="status" className="notice ok">
          {t('settings.agents.revokedAll', { count: revokeAll.data.revoked })}
        </p>
      ) : null}
    </Section>
  )
}

/**
 * Grant write access, in a dialog: the client, a name for it, one capability, and the sentence
 * that says what it will be able to do. It opens by itself when an agent's link filled it in.
 */
function GrantDialog({
  orgId,
  orgName,
  client,
  capability: asked,
  admin,
}: {
  orgId: string
  orgName: string
  client?: string | undefined
  capability?: AgentCapability | undefined
  /** An owner or admin: only they may grant what only they may do (MANAGER_ONLY). */
  admin: boolean
}) {
  const { t } = useI18n()
  const create = useCreateAgentGrant(orgId)
  const ref = useRef<HTMLDialogElement>(null)
  const [clientId, setClientId] = useState(client ?? '')
  const [name, setName] = useState('')
  /** What the link asked for, unless the caller may not grant it: then nothing is chosen. */
  const allowed = (c: AgentCapability | undefined) =>
    c && MANAGER_ONLY.includes(c) && !admin ? undefined : c
  const [capability, setCapability] = useState<AgentCapability | undefined>(
    asked ? allowed(asked) : 'dashboards:write',
  )
  const ids = {
    title: useId(),
    client: useId(),
    name: useId(),
    help: useId(),
    access: useId(),
    only: useId(),
  }
  useEffect(() => {
    setClientId(client ?? '')
    if (client && !ref.current?.open) ref.current?.showModal()
  }, [client])
  // Again once the role is known: a member's link to a manager-only capability chooses nothing.
  // biome-ignore lint/correctness/useExhaustiveDependencies: allowed reads admin, listed here
  useEffect(() => {
    setCapability(asked ? allowed(asked) : 'dashboards:write')
  }, [asked, admin])
  const ready =
    CLIENT_ID.test(clientId.trim()) && name.trim().length <= 100 && capability !== undefined
  return (
    <>
      <Btn
        variant="ghost"
        size="sm"
        onClick={() => {
          create.reset()
          ref.current?.showModal()
        }}
      >
        {t('settings.agents.grant')}
      </Btn>
      <dialog ref={ref} aria-labelledby={ids.title}>
        <form
          className="stack"
          style={{ gap: 14 }}
          onSubmit={(e) => {
            e.preventDefault()
            if (!ready || !capability) return
            create.mutate(
              { client_id: clientId.trim(), client_name: name.trim(), capability },
              {
                onSuccess: () => {
                  setClientId('')
                  setName('')
                  ref.current?.close()
                },
              },
            )
          }}
        >
          <h2 id={ids.title} style={{ fontSize: 17 }}>
            {t('settings.agents.grant')}
          </h2>
          <p className="dim" style={{ fontSize: 13.5 }}>
            {t('settings.agents.body')}
          </p>
          <div className="field">
            <label htmlFor={ids.client}>{t('settings.agents.clientId')}</label>
            <input
              id={ids.client}
              className="input mono"
              translate="no"
              autoComplete="off"
              spellCheck={false}
              maxLength={256}
              aria-describedby={ids.help}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
            <span id={ids.help} className="hint">
              {t('settings.agents.clientIdHint')}
            </span>
          </div>
          <div className="field">
            <label htmlFor={ids.name}>{t('settings.agents.name')}</label>
            <input
              id={ids.name}
              className="input"
              maxLength={100}
              placeholder={t('settings.agents.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <fieldset className="ag-caps">
            <legend id={ids.access}>{t('settings.agents.access')}</legend>
            <ul>
              {AGENT_CAPABILITIES.map((c) => {
                const off = MANAGER_ONLY.includes(c) && !admin
                return (
                  <li key={c}>
                    <label className={off ? 'radio off' : 'radio'}>
                      <input
                        type="radio"
                        name="capability"
                        value={c}
                        checked={capability === c}
                        disabled={off}
                        aria-describedby={off ? ids.only : undefined}
                        onChange={() => setCapability(c)}
                      />
                      <span>{t(CAPABILITY[c].label)}</span>
                    </label>
                  </li>
                )
              })}
            </ul>
            {admin ? null : (
              <span id={ids.only} className="hint">
                {t('settings.agents.instrumentationOnly')}
              </span>
            )}
          </fieldset>
          {ready && capability ? (
            <p className="se-hint">
              {t('settings.agents.confirmBody', {
                client: clientId.trim(),
                org: orgName,
                access: t(CAPABILITY[capability].verb),
              })}
            </p>
          ) : null}
          {create.error ? (
            <p role="alert" className="notice error">
              {isApiError(create.error, 409)
                ? t('settings.agents.exists')
                : isApiError(create.error, 429)
                  ? t('settings.agents.tooMany')
                  : isApiError(create.error, 400)
                    ? t('settings.agents.invalid')
                    : isApiError(create.error, 403)
                      ? t('settings.agents.instrumentationOnly')
                      : t('state.failed')}
            </p>
          ) : null}
          <div className="head-actions">
            <button type="submit" className="btn solid" disabled={!ready || create.isPending}>
              {t('settings.agents.confirm')}
            </button>
            <button type="button" className="btn" onClick={() => ref.current?.close()}>
              {t('settings.cancel')}
            </button>
          </div>
        </form>
      </dialog>
      {create.isSuccess ? (
        <span role="status" className="st ok">
          {t('settings.agents.granted')}
        </span>
      ) : null}
    </>
  )
}
